import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useParams, useNavigate, Outlet } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { TenantPathProvider } from './context/TenantPathContext';
import { useBranding } from './theme/BrandColorContext';
import api from './api/client';
import { Box, CircularProgress, Typography } from '@mui/material';
import Layout from './components/Layout';
import ConsoleLayout from './components/ConsoleLayout';
import AppSkeleton from './components/AppSkeleton';
import Login from './pages/Login';
import AcceptInvite from './pages/AcceptInvite';
import ResetPassword from './pages/ResetPassword';
import Register from './pages/Register';
import Onboarding from './pages/Onboarding';
import ImportEmployees from './pages/ImportEmployees';
import Dashboard from './pages/Dashboard';
import CampaignTemplates from './pages/CampaignTemplates';
import CampaignWizard from './pages/CampaignWizard';
import CampaignDetail from './pages/CampaignDetail';
import CampaignPreview from './pages/CampaignPreview';
import Employees from './pages/Employees';
import EmployeeDetail from './pages/EmployeeDetail';
import Integrations from './pages/Integrations';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import CampaignLanding from './pages/CampaignLanding';
import LandingConfigurator from './pages/LandingConfigurator';
import ConsoleClients from './pages/ConsoleClients';
import ConsoleClientOnboarding from './pages/ConsoleClientOnboarding';

function ProtectedRoute({ children, allowPlatformAdmin, requireTenantContext }) {
  const { user, loading, isPlatformAdmin, activeTenantId } = useAuth();

  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;

  if (allowPlatformAdmin) {
    if (!isPlatformAdmin) {
      return <Navigate to="/login" replace />;
    }
    if (requireTenantContext && !activeTenantId) {
      return <Navigate to="/console/clients" replace />;
    }
    return children;
  }

  if (isPlatformAdmin && !activeTenantId) {
    return <Navigate to="/console/clients" replace />;
  }

  return children;
}

/**
 * Single source of truth for "which tenant's branding should be shown".
 * Reacts to `activeTenantId` from AuthContext — which already correctly
 * resolves the tenant from hostname, login response, or the `/:tenantId/*`
 * route param — instead of re-guessing the tenant from the URL path
 * independently (which broke for bare routes like `/campaigns`).
 * Re-fetches whenever the active tenant changes, so switching tenants
 * (platform admin "enter tenant", or logging into a different tenant)
 * always applies the correct branding instead of leaving stale/default
 * styles applied.
 */
function BrandingSync() {
  const { activeTenantId, loading: authLoading } = useAuth();
  const { setBranding, resetBranding, setBrandingLoading } = useBranding();

  useEffect(() => {
    if (authLoading) return;

    if (!activeTenantId) {
      resetBranding();
      setBrandingLoading(false);
      return;
    }

    let cancelled = false;
    setBrandingLoading(true);
    api
      .get(`/api/tenants/lookup?id=${encodeURIComponent(activeTenantId)}`)
      .then((tenant) => {
        if (cancelled) return;
        if (tenant?.branding) {
          const parsed =
            typeof tenant.branding === 'string' ? JSON.parse(tenant.branding) : tenant.branding;
          setBranding(parsed);
        } else {
          resetBranding();
        }
      })
      .catch(() => {
        if (!cancelled) resetBranding();
      })
      .finally(() => {
        if (!cancelled) setBrandingLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeTenantId, authLoading, setBranding, resetBranding, setBrandingLoading]);

  return null;
}

function tenantAppElement(tenantId) {
  return (
    <ProtectedRoute>
      <TenantPathProvider tenantId={tenantId}>
        <Layout />
      </TenantPathProvider>
    </ProtectedRoute>
  );
}

function TenantRoutes() {
  return tenantAppElement(null);
}

function TenantAppRoutes() {
  const { tenantId } = useParams();
  const { setActiveTenantId } = useAuth();

  useEffect(() => {
    if (tenantId) {
      setActiveTenantId(tenantId);
    }
  }, [tenantId, setActiveTenantId]);

  return tenantAppElement(tenantId);
}

function ConsoleRoutes() {
  return (
    <ProtectedRoute allowPlatformAdmin>
      <ConsoleLayout />
    </ProtectedRoute>
  );
}

function TenantResolver() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading, setActiveTenantId } = useAuth();
  const [status, setStatus] = useState('resolving');
  const [error, setError] = useState('');

  useEffect(() => {
    if (authLoading) return;

    setStatus('resolving');
    api
      .get(`/api/tenants/lookup?slug=${encodeURIComponent(slug)}`)
      .then((tenant) => {
        setActiveTenantId(tenant.id);
        if (user) {
          navigate('/campaigns', { replace: true });
        } else {
          navigate('/login', { replace: true });
        }
      })
      .catch((err) => {
        setStatus('error');
        setError(err.body?.error || err.message || 'Nie znaleziono tenantu');
      });
  }, [slug, user, authLoading, navigate, setActiveTenantId]);

  if (status === 'error') {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
      <CircularProgress />
    </Box>
  );
}

function RootRedirect() {
  const { user, loading, activeTenantId } = useAuth();
  const hostname = window.location.hostname || '';
  const isPanelHost = hostname.startsWith('panel.');
  if (loading) return null;
  if (hostname.startsWith('console.') || user?.role === 'platformadmin') {
    if (isPanelHost && activeTenantId) {
      return <Navigate to={`/${activeTenantId}/campaigns`} replace />;
    }
    return <Navigate to="/console/clients" replace />;
  }
  if (activeTenantId) {
    return <Navigate to={`/${activeTenantId}/campaigns`} replace />;
  }
  return <Navigate to="/campaigns" replace />;
}

function TenantIndexRedirect() {
  return <Navigate to="campaigns" replace />;
}

function App() {
  const { loading: authLoading } = useAuth();
  const { brandingLoading } = useBranding();

  if (authLoading || brandingLoading) {
    return (
      <>
        <BrandingSync />
        <AppSkeleton />
      </>
    );
  }

  return (
    <>
      <BrandingSync />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/invite" element={<AcceptInvite />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/register" element={<Register />} />
        <Route path="/onboarding" element={<Onboarding />} />
        <Route path="/import" element={<ImportEmployees />} />
        <Route path="/landing/:code" element={<CampaignLanding />} />
        <Route path="/tenant/:slug" element={<TenantResolver />} />

        <Route path="/console" element={<ConsoleRoutes />}>
          <Route index element={<Navigate to="/console/clients" replace />} />
          <Route path="clients" element={<ConsoleClients />} />
          <Route path="clients/new" element={<ConsoleClientOnboarding />} />
        </Route>

        <Route path="/" element={<TenantRoutes />}>
          <Route index element={<RootRedirect />} />
          <Route path="campaigns" element={<Dashboard />} />
          <Route path="campaigns/templates" element={<CampaignTemplates />} />
          <Route path="campaigns/new" element={<CampaignWizard />} />
          <Route path="campaigns/:id" element={<CampaignDetail />} />
          <Route path="campaigns/:id/preview" element={<CampaignPreview />} />
          <Route path="campaigns/:id/landing" element={<LandingConfigurator />} />
          <Route path="employees" element={<Employees />} />
          <Route path="employees/:id" element={<EmployeeDetail />} />
          <Route path="integrations" element={<Integrations />} />
          <Route path="reports" element={<Reports />} />
          <Route path="settings" element={<Settings />} />
          <Route path="*" element={<Navigate to="/campaigns" replace />} />
        </Route>

        <Route path="/:tenantId/*" element={<TenantAppRoutes />}>
          <Route index element={<TenantIndexRedirect />} />
          <Route path="campaigns" element={<Dashboard />} />
          <Route path="campaigns/templates" element={<CampaignTemplates />} />
          <Route path="campaigns/new" element={<CampaignWizard />} />
          <Route path="campaigns/:id" element={<CampaignDetail />} />
          <Route path="campaigns/:id/preview" element={<CampaignPreview />} />
          <Route path="campaigns/:id/landing" element={<LandingConfigurator />} />
          <Route path="employees" element={<Employees />} />
          <Route path="employees/:id" element={<EmployeeDetail />} />
          <Route path="integrations" element={<Integrations />} />
          <Route path="reports" element={<Reports />} />
          <Route path="settings" element={<Settings />} />
          <Route path="*" element={<Navigate to="campaigns" replace />} />
        </Route>
      </Routes>
    </>
  );
}

export default App;
